import React, { useState, useEffect, useCallback } from 'react'
import {
  BarChart3,
  Play,
  RefreshCw,
  Settings,
  Database,
  GitBranch,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowUpDown,
  Download,
  Plus,
  Trash2,
  Eye,
  AlertTriangle,
  Info,
  TrendingUp,
  FileText,
  ChevronLeft,
  ChevronRight,
  Filter,
  Edit2,
  Calendar,
  CheckSquare,
  Square
} from 'lucide-react'
import clsx from 'clsx'
import * as adminApi from '../../services/adminApi'
import StatusBadge from '../../components/admin/StatusBadge'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import Toast, { ToastContainer } from '../../components/admin/Toast'

// Score level configurations
const scoreConfig = {
  excellent: { min: 0.85, color: 'text-green-600', bg: 'bg-green-100', label: 'Excellent' },
  good: { min: 0.75, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Good' },
  acceptable: { min: 0.65, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Acceptable' },
  poor: { min: 0, color: 'text-red-600', bg: 'bg-red-100', label: 'Poor' }
}

const getScoreLevel = (score) => {
  if (score === null || score === undefined) return null
  if (score >= scoreConfig.excellent.min) return 'excellent'
  if (score >= scoreConfig.good.min) return 'good'
  if (score >= scoreConfig.acceptable.min) return 'acceptable'
  return 'poor'
}

// Domain configuration
const domainConfig = {
  general: { label: 'General', color: 'bg-gray-500' },
  technical: { label: 'Technical', color: 'bg-blue-500' },
  conversational: { label: 'Conversational', color: 'bg-green-500' },
  legal: { label: 'Legal', color: 'bg-purple-500' },
  medical: { label: 'Medical', color: 'bg-red-500' },
  ecommerce: { label: 'E-commerce', color: 'bg-orange-500' },
  financial: { label: 'Financial', color: 'bg-yellow-600' },
  marketing: { label: 'Marketing', color: 'bg-pink-500' },
  news: { label: 'News', color: 'bg-indigo-500' }
}

const ScoreBadge = ({ score, metric = 'composite' }) => {
  if (score === null || score === undefined) {
    return <span className="text-gray-400">-</span>
  }

  const level = getScoreLevel(score)
  const config = scoreConfig[level]
  const displayScore = metric === 'comet' ? score.toFixed(3) :
    metric === 'composite' ? score.toFixed(3) :
      score.toFixed(1)

  return (
    <span className={clsx(
      'inline-flex items-center px-2 py-0.5 rounded text-sm font-medium',
      config.bg, config.color
    )}>
      {displayScore}
    </span>
  )
}

const QualityBenchmarksPage = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState('dashboard')

  // Data states
  const [runs, setRuns] = useState([])
  const [scores, setScores] = useState([])
  const [versions, setVersions] = useState([])
  const [activeVersion, setActiveVersion] = useState(null)
  const [datasets, setDatasets] = useState([])
  const [accounts, setAccounts] = useState([])

  // UI states
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [toasts, setToasts] = useState([])

  // Modal states
  const [showRunModal, setShowRunModal] = useState(false)
  const [showSyncModal, setShowSyncModal] = useState(false)
  const [showVersionModal, setShowVersionModal] = useState(false)
  const [showResultsModal, setShowResultsModal] = useState(false)
  const [selectedRun, setSelectedRun] = useState(null)
  const [runResults, setRunResults] = useState([])

  // New run form state
  const [newRun, setNewRun] = useState({
    account_id: '',
    dataset_name: 'flores',
    source_lang: 'en-US',
    target_lang: '',
    domain: '',
    sample_limit: 100
  })

  // Sync form state
  const [syncForm, setSyncForm] = useState({
    dataset_name: 'flores',
    source_lang: 'en-US',
    target_langs: [],  // Changed to array for multi-select
    limit: 200,
    clear_existing: false
  })

  // Sync progress state
  const [syncProgress, setSyncProgress] = useState({
    isRunning: false,
    currentPairIndex: 0,
    totalPairs: 0,
    currentPair: null,
    results: [],  // {pair, status, samplesImported, error}
    completed: false
  })

  // Delete confirm
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, run: null })

  // Samples state for samples tab
  const [samples, setSamples] = useState([])
  const [samplesLoading, setSamplesLoading] = useState(false)
  const [samplesPagination, setSamplesPagination] = useState({ offset: 0, limit: 25, total: 0 })
  const [samplesFilter, setSamplesFilter] = useState({ languagePair: '', domain: '', datasetId: '', sourceOrigin: '' })
  const [availableLanguagePairs, setAvailableLanguagePairs] = useState([])

  // Sample CRUD state
  const [showSampleEditModal, setShowSampleEditModal] = useState(false)
  const [editingSample, setEditingSample] = useState(null)
  const [deleteSampleConfirm, setDeleteSampleConfirm] = useState({ open: false, sample: null })

  // Sample selection state
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedSampleIds, setSelectedSampleIds] = useState(new Set())

  // Language pair sample counts for run validation
  const [languagePairCounts, setLanguagePairCounts] = useState([])

  // Supported languages for sync modal (T8R supported)
  const [supportedLanguages, setSupportedLanguages] = useState([])
  // Dataset-specific available languages (intersection of dataset + T8R)
  const [datasetLanguages, setDatasetLanguages] = useState([])
  const [datasetLanguagesLoading, setDatasetLanguagesLoading] = useState(false)

  // Domain mappings for sync
  const [domainMappings, setDomainMappings] = useState({})
  const [supportedDomains, setSupportedDomains] = useState([])
  const [showDomainMappings, setShowDomainMappings] = useState(false)

  // Scheduling state for new run
  const [scheduleRun, setScheduleRun] = useState(false)
  const [scheduledDateTime, setScheduledDateTime] = useState('')
  const [useSelectedSamples, setUseSelectedSamples] = useState(false)

  // Toast helper
  const addToast = useCallback((message, type = 'info') => {
    const id = Date.now()
    setToasts(prev => [...prev, { id, message, type }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [runsData, scoresData, versionsData, activeVersionData, datasetsData] = await Promise.all([
        adminApi.getBenchmarkRuns({ limit: 50 }).catch(() => []),
        adminApi.getBenchmarkScores({}).catch(() => []),
        adminApi.getBenchmarkVersions({}).catch(() => []),
        adminApi.getActiveBenchmarkVersion().catch(() => null),
        adminApi.getBenchmarkDatasets().catch(() => [])
      ])

      setRuns(runsData || [])
      setScores(scoresData || [])
      setVersions(versionsData || [])
      setActiveVersion(activeVersionData)
      setDatasets(datasetsData || [])

      // Fetch accounts for run creation - accounts are embedded in configs response
      try {
        const configs = await adminApi.getConfigs()
        const allAccounts = []
        for (const config of configs) {
          // Use embedded accounts from config response (more efficient than separate API calls)
          const configAccounts = config.accounts || []
          allAccounts.push(...configAccounts.filter(a => a.is_active))
        }
        setAccounts(allAccounts)
      } catch (err) {
        console.error('Failed to fetch accounts:', err)
        setAccounts([])
      }

      // Fetch supported languages for sync modal
      try {
        const languages = await adminApi.getLanguages()
        // Deduplicate by locale (same language may appear for multiple providers)
        const uniqueLocales = new Map()
        for (const lang of (languages || [])) {
          if (!uniqueLocales.has(lang.locale)) {
            uniqueLocales.set(lang.locale, lang)
          }
        }
        // Sort by locale for consistent ordering
        const sortedLanguages = Array.from(uniqueLocales.values()).sort((a, b) => a.locale.localeCompare(b.locale))
        setSupportedLanguages(sortedLanguages)
      } catch (err) {
        console.error('Failed to fetch supported languages:', err)
        setSupportedLanguages([])
      }

      // Fetch domain mappings for sync
      try {
        const mappingData = await adminApi.getBenchmarkDomainMappings()
        setDomainMappings(mappingData.mappings || {})
        setSupportedDomains(mappingData.supported_domains || [])
      } catch (err) {
        console.error('Failed to fetch domain mappings:', err)
        setDomainMappings({})
        setSupportedDomains([])
      }

    } catch (err) {
      console.error('Failed to fetch benchmark data:', err)
      setError('Failed to load benchmark data')
      addToast('Failed to load benchmark data', 'error')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  // Initial load
  useEffect(() => {
    fetchData()
  }, [fetchData])

  // Fetch dataset-specific languages when dataset selection changes or sync modal opens
  const fetchDatasetLanguages = useCallback(async (datasetName) => {
    if (!datasetName) return

    setDatasetLanguagesLoading(true)
    try {
      const data = await adminApi.getBenchmarkDatasetLanguages(datasetName)
      // data contains source_languages and target_languages arrays
      setDatasetLanguages(data.source_languages || [])
    } catch (err) {
      console.error('Failed to fetch dataset languages:', err)
      // Fall back to T8R supported languages
      setDatasetLanguages([])
      addToast('Failed to load dataset languages, showing all T8R languages', 'warning')
    } finally {
      setDatasetLanguagesLoading(false)
    }
  }, [addToast])

  // Fetch dataset languages when sync modal opens or dataset changes
  useEffect(() => {
    if (showSyncModal && syncForm.dataset_name) {
      fetchDatasetLanguages(syncForm.dataset_name)
    }
  }, [showSyncModal, syncForm.dataset_name, fetchDatasetLanguages])

  // Create new benchmark run
  const handleCreateRun = async () => {
    if (!newRun.account_id || !newRun.target_lang) {
      addToast('Please select an account and target language', 'warning')
      return
    }

    try {
      const response = await adminApi.createBenchmarkRun({
        account_id: parseInt(newRun.account_id),
        dataset_name: newRun.dataset_name,
        source_lang: newRun.source_lang,
        target_lang: newRun.target_lang,
        domain: newRun.domain || null,
        sample_limit: newRun.sample_limit
      })

      addToast(`Benchmark run started: ${response.id}`, 'success')
      setShowRunModal(false)
      setNewRun({
        account_id: '',
        dataset_name: 'flores',
        source_lang: 'en-US',
        target_lang: '',
        domain: '',
        sample_limit: 100
      })
      fetchData()
    } catch (err) {
      console.error('Failed to create benchmark run:', err)
      addToast(err.response?.data?.detail || 'Failed to create benchmark run', 'error')
    }
  }

  // Sync dataset - handles multiple pairs sequentially
  const handleSyncDataset = async () => {
    if (!syncForm.source_lang || syncForm.target_langs.length === 0) {
      addToast('Please select source and at least one target language', 'warning')
      return
    }

    const targetLangs = syncForm.target_langs
    const totalPairs = targetLangs.length

    // Initialize progress state
    setSyncProgress({
      isRunning: true,
      currentPairIndex: 0,
      totalPairs,
      currentPair: `${syncForm.source_lang} → ${targetLangs[0]}`,
      results: [],
      completed: false
    })

    const results = []

    // Process each language pair sequentially
    for (let i = 0; i < targetLangs.length; i++) {
      const targetLang = targetLangs[i]
      const pairLabel = `${syncForm.source_lang} → ${targetLang}`

      // Update current progress
      setSyncProgress(prev => ({
        ...prev,
        currentPairIndex: i,
        currentPair: pairLabel
      }))

      try {
        const response = await adminApi.syncBenchmarkDataset({
          dataset_name: syncForm.dataset_name,
          source_lang: syncForm.source_lang,
          target_lang: targetLang,
          limit: syncForm.limit,
          clear_existing: syncForm.clear_existing
        })

        if (response.status === 'completed') {
          results.push({
            pair: pairLabel,
            status: 'success',
            samplesImported: response.samples_imported,
            error: null
          })
        } else {
          results.push({
            pair: pairLabel,
            status: 'failed',
            samplesImported: 0,
            error: response.error || 'Unknown error'
          })
        }
      } catch (err) {
        console.error(`Failed to sync ${pairLabel}:`, err)
        results.push({
          pair: pairLabel,
          status: 'failed',
          samplesImported: 0,
          error: err.response?.data?.detail || 'Sync failed'
        })
      }

      // Update results in progress
      setSyncProgress(prev => ({
        ...prev,
        results: [...results]
      }))
    }

    // Mark as completed
    setSyncProgress(prev => ({
      ...prev,
      isRunning: false,
      completed: true,
      currentPair: null
    }))

    // Show summary toast
    const successCount = results.filter(r => r.status === 'success').length
    const totalSamples = results.reduce((sum, r) => sum + r.samplesImported, 0)
    if (successCount === totalPairs) {
      addToast(`Successfully synced ${totalPairs} language pairs (${totalSamples} samples)`, 'success')
    } else if (successCount > 0) {
      addToast(`Synced ${successCount}/${totalPairs} pairs (${totalSamples} samples), some failed`, 'warning')
    } else {
      addToast('All sync operations failed', 'error')
    }

    // Refresh data
    fetchData()
  }

  // Reset sync modal state
  const handleCloseSyncModal = () => {
    setShowSyncModal(false)
    setSyncProgress({
      isRunning: false,
      currentPairIndex: 0,
      totalPairs: 0,
      currentPair: null,
      results: [],
      completed: false
    })
    setSyncForm({
      dataset_name: syncForm.dataset_name,  // Keep dataset selection
      source_lang: 'en-US',
      target_langs: [],
      limit: 200,
      clear_existing: false
    })
  }

  // Toggle target language selection
  const toggleTargetLang = (locale) => {
    setSyncForm(prev => {
      const newTargetLangs = prev.target_langs.includes(locale)
        ? prev.target_langs.filter(l => l !== locale)
        : [...prev.target_langs, locale]
      return { ...prev, target_langs: newTargetLangs }
    })
  }

  // Select/deselect all target languages
  const selectAllTargetLangs = () => {
    const availableLangs = datasetLanguages.filter(l => l !== syncForm.source_lang)
    setSyncForm(prev => ({ ...prev, target_langs: availableLangs }))
  }

  const deselectAllTargetLangs = () => {
    setSyncForm(prev => ({ ...prev, target_langs: [] }))
  }

  // Delete benchmark run
  const handleDeleteRun = async () => {
    if (!deleteConfirm.run) return

    try {
      await adminApi.deleteBenchmarkRun(deleteConfirm.run.id)
      addToast('Benchmark run deleted', 'success')
      setDeleteConfirm({ open: false, run: null })
      fetchData()
    } catch (err) {
      console.error('Failed to delete run:', err)
      addToast(err.response?.data?.detail || 'Failed to delete run', 'error')
    }
  }

  // View run results
  const handleViewResults = async (run) => {
    try {
      const results = await adminApi.getBenchmarkRunResults(run.id, { limit: 100 })
      setRunResults(results)
      setSelectedRun(run)
      setShowResultsModal(true)
    } catch (err) {
      console.error('Failed to fetch results:', err)
      addToast('Failed to load run results', 'error')
    }
  }

  // Sample CRUD handlers
  const handleAddSample = () => {
    setEditingSample(null)
    setShowSampleEditModal(true)
  }

  const handleEditSample = (sample) => {
    setEditingSample(sample)
    setShowSampleEditModal(true)
  }

  const handleSaveSample = async (sampleData) => {
    try {
      if (editingSample) {
        await adminApi.updateBenchmarkSample(editingSample.id, sampleData)
        addToast('Sample updated successfully', 'success')
      } else {
        await adminApi.createBenchmarkSample(sampleData)
        addToast('Sample created successfully', 'success')
      }
      setShowSampleEditModal(false)
      fetchSamples(samplesPagination.offset)
      fetchLanguagePairCounts()
    } catch (err) {
      console.error('Failed to save sample:', err)
      addToast(err.response?.data?.detail || 'Failed to save sample', 'error')
      throw err
    }
  }

  const handleDeleteSample = async () => {
    if (!deleteSampleConfirm.sample) return
    try {
      await adminApi.deleteBenchmarkSample(deleteSampleConfirm.sample.id)
      addToast('Sample deleted successfully', 'success')
      setDeleteSampleConfirm({ open: false, sample: null })
      fetchSamples(samplesPagination.offset)
      fetchLanguagePairCounts()
    } catch (err) {
      console.error('Failed to delete sample:', err)
      addToast('Failed to delete sample', 'error')
    }
  }

  // Sample selection handlers
  const toggleSampleSelection = (sampleId) => {
    setSelectedSampleIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(sampleId)) {
        newSet.delete(sampleId)
      } else {
        newSet.add(sampleId)
      }
      return newSet
    })
  }

  const selectAllSamples = () => {
    setSelectedSampleIds(new Set(samples.map(s => s.id)))
  }

  const deselectAllSamples = () => {
    setSelectedSampleIds(new Set())
  }

  // Bulk delete confirmation state
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState({ open: false })
  // Reclassify modal state
  const [showReclassifyModal, setShowReclassifyModal] = useState(false)
  const [reclassifyDomain, setReclassifyDomain] = useState('')

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedSampleIds.size === 0) return
    try {
      const result = await adminApi.bulkDeleteBenchmarkSamples([...selectedSampleIds])
      addToast(`Deleted ${result.deleted_count} samples${result.failed_count > 0 ? `, ${result.failed_count} failed` : ''}`,
        result.failed_count > 0 ? 'warning' : 'success')
      setBulkDeleteConfirm({ open: false })
      setSelectedSampleIds(new Set())
      setSelectionMode(false)
      fetchSamples(0)
      fetchLanguagePairCounts()
    } catch (err) {
      console.error('Failed to bulk delete samples:', err)
      addToast('Failed to delete samples', 'error')
    }
  }

  // Bulk reclassify handler
  const handleBulkReclassify = async () => {
    if (selectedSampleIds.size === 0 || !reclassifyDomain) return
    try {
      const result = await adminApi.bulkReclassifyBenchmarkSamples([...selectedSampleIds], reclassifyDomain)
      addToast(`Reclassified ${result.updated_count} samples to "${reclassifyDomain}"${result.failed_count > 0 ? `, ${result.failed_count} failed` : ''}`,
        result.failed_count > 0 ? 'warning' : 'success')
      setShowReclassifyModal(false)
      setReclassifyDomain('')
      setSelectedSampleIds(new Set())
      setSelectionMode(false)
      fetchSamples(samplesPagination.offset)
    } catch (err) {
      console.error('Failed to reclassify samples:', err)
      addToast('Failed to reclassify samples', 'error')
    }
  }

  // Fetch samples for the Samples tab
  const fetchSamples = useCallback(async (offset = 0) => {
    setSamplesLoading(true)
    try {
      const { languagePair, domain, datasetId, sourceOrigin } = samplesFilter
      const result = await adminApi.getBenchmarkSamples(
        domain || null,
        languagePair || null,
        true, // is_active
        samplesPagination.limit,
        offset,
        datasetId || null,
        sourceOrigin || null
      )
      setSamples(result.samples || result || [])
      setSamplesPagination(prev => ({
        ...prev,
        offset,
        total: result.total || (result.samples ? result.samples.length : result.length)
      }))
    } catch (err) {
      console.error('Failed to fetch samples:', err)
      addToast('Failed to load samples', 'error')
      setSamples([])
    } finally {
      setSamplesLoading(false)
    }
  }, [samplesFilter, samplesPagination.limit, addToast])

  // Fetch language pair sample counts
  const fetchLanguagePairCounts = useCallback(async () => {
    try {
      const result = await adminApi.getBenchmarkLanguagePairSampleCounts()
      setLanguagePairCounts(result.counts || [])
    } catch (err) {
      console.error('Failed to fetch language pair counts:', err)
    }
  }, [])

  // Fetch available language pairs for filter dropdown
  const fetchLanguagePairs = useCallback(async () => {
    try {
      const result = await adminApi.getBenchmarkLanguagePairs()
      // API returns {language_pairs: [...], in_database: [...], available_defaults: [...]}
      setAvailableLanguagePairs(result?.language_pairs || result?.in_database || [])
    } catch (err) {
      console.error('Failed to fetch language pairs:', err)
    }
  }, [])

  // Track if samples tab has been initialized
  const samplesInitializedRef = React.useRef(false)

  // Load samples when switching to samples tab (only on first load)
  useEffect(() => {
    if (activeTab === 'samples' && !samplesInitializedRef.current && !samplesLoading) {
      samplesInitializedRef.current = true
      fetchSamples(0)
      fetchLanguagePairs()
      fetchLanguagePairCounts()
    }
  }, [activeTab, samplesLoading, fetchSamples, fetchLanguagePairs, fetchLanguagePairCounts])

  // Re-fetch samples when filter changes (skip if samples tab not yet initialized)
  useEffect(() => {
    if (activeTab === 'samples' && samplesInitializedRef.current && !samplesLoading) {
      fetchSamples(0)
    }
  }, [samplesFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Render status badge
  const renderStatus = (status) => {
    const statusConfig = {
      pending: { color: 'bg-gray-100 text-gray-800', icon: Clock },
      scheduled: { color: 'bg-yellow-100 text-yellow-800', icon: Calendar },
      running: { color: 'bg-blue-100 text-blue-800', icon: Loader2 },
      completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle }
    }
    const config = statusConfig[status] || statusConfig.pending
    const Icon = config.icon

    return (
      <span className={clsx('inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium', config.color)}>
        <Icon className={clsx('w-3 h-3', status === 'running' && 'animate-spin')} />
        {status}
      </span>
    )
  }

  // Dashboard tab content
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Runs</p>
              <p className="text-2xl font-semibold">{runs.length}</p>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-semibold">{runs.filter(r => r.status === 'completed').length}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Version</p>
              <p className="text-2xl font-semibold">{activeVersion?.version_code || '-'}</p>
            </div>
            <GitBranch className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Datasets</p>
              <p className="text-2xl font-semibold">{datasets.length}</p>
            </div>
            <Database className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Account Scores */}
      {scores.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="font-medium">Account Benchmark Scores</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language Pair</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Runs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg COMET</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg chrF</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg BLEU</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Composite</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scores.map((score, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{score.account_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{score.language_pair}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{score.total_runs}</td>
                    <td className="px-4 py-3"><ScoreBadge score={score.avg_comet} metric="comet" /></td>
                    <td className="px-4 py-3"><ScoreBadge score={score.avg_chrf} metric="chrf" /></td>
                    <td className="px-4 py-3"><ScoreBadge score={score.avg_bleu} metric="bleu" /></td>
                    <td className="px-4 py-3"><ScoreBadge score={score.avg_composite} metric="composite" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Runs */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 py-3 border-b flex justify-between items-center">
          <h3 className="font-medium">Recent Benchmark Runs</h3>
          <button
            onClick={() => setShowRunModal(true)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Run
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Languages</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Samples</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">COMET</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {runs.slice(0, 10).map((run) => (
                <tr key={run.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-500">#{run.id}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{run.account_name || `Account ${run.account_id}`}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{run.source_lang} → {run.target_lang}</td>
                  <td className="px-4 py-3 text-sm text-gray-500">{run.samples_evaluated}</td>
                  <td className="px-4 py-3">{renderStatus(run.status)}</td>
                  <td className="px-4 py-3"><ScoreBadge score={run.avg_comet_score} metric="comet" /></td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(run.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {run.status === 'completed' && (
                        <button
                          onClick={() => handleViewResults(run)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Results"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      {run.status !== 'running' && (
                        <button
                          onClick={() => setDeleteConfirm({ open: true, run })}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  // Datasets tab content
  const renderDatasets = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Benchmark Datasets</h3>
        <button
          onClick={() => setShowSyncModal(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
        >
          <Download className="w-4 h-4" />
          Sync Dataset
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {datasets.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow p-8 text-center">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No datasets synced yet.</p>
            <p className="text-sm text-gray-400 mt-2">Click "Sync Dataset" to import FLORES+ data.</p>
          </div>
        ) : (
          datasets.map((dataset) => (
            <div key={dataset.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{dataset.display_name}</h4>
                  <p className="text-sm text-gray-500">{dataset.name}</p>
                </div>
                <StatusBadge status={dataset.is_active ? 'active' : 'inactive'} />
              </div>
              <div className="mt-4 space-y-2 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Languages:</span>
                  <span className="font-medium">{dataset.languages_count || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Samples:</span>
                  <span className="font-medium">{dataset.samples_count?.toLocaleString() || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Last Synced:</span>
                  <span className="font-medium">
                    {dataset.last_synced_at
                      ? new Date(dataset.last_synced_at).toLocaleDateString()
                      : 'Never'}
                  </span>
                </div>
              </div>
              {dataset.license && (
                <p className="mt-3 text-xs text-gray-400">License: {dataset.license}</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )

  // Samples tab content
  const renderSamples = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h3 className="text-lg font-medium">Benchmark Samples</h3>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Language Pair Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={samplesFilter.languagePair}
              onChange={(e) => setSamplesFilter(prev => ({ ...prev, languagePair: e.target.value }))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value="">All Language Pairs</option>
              {availableLanguagePairs.map((pair) => (
                <option key={pair} value={pair}>{pair}</option>
              ))}
            </select>
          </div>
          {/* Source Origin Filter */}
          <select
            value={samplesFilter.sourceOrigin}
            onChange={(e) => setSamplesFilter(prev => ({ ...prev, sourceOrigin: e.target.value }))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            <option value="">All Sources</option>
            <option value="custom">Custom</option>
            <option value="flores">FLORES+</option>
            <option value="ntrex">NTREX</option>
            <option value="wmt">WMT</option>
            <option value="emea">EMEA</option>
            <option value="europarl">Europarl</option>
            <option value="opensubtitles">OpenSubtitles</option>
            <option value="europat">EuroPat</option>
            <option value="news_commentary">News Commentary</option>
          </select>
          {/* Selection Mode Toggle */}
          <button
            onClick={() => {
              setSelectionMode(!selectionMode)
              if (selectionMode) setSelectedSampleIds(new Set())
            }}
            className={clsx(
              'inline-flex items-center gap-1 px-3 py-1.5 border rounded text-sm',
              selectionMode ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-300 hover:bg-gray-50'
            )}
          >
            {selectionMode ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            Select
          </button>
          <button
            onClick={() => fetchSamples(0)}
            className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleAddSample}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            Add Sample
          </button>
        </div>
      </div>

      {/* Selection Bar */}
      {selectionMode && (
        <div className="bg-blue-50 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm text-blue-700">
              <strong>{selectedSampleIds.size}</strong> samples selected
            </span>
            <button onClick={selectAllSamples} className="text-sm text-blue-600 hover:underline">
              Select All on Page
            </button>
            <button onClick={deselectAllSamples} className="text-sm text-blue-600 hover:underline">
              Deselect All
            </button>
          </div>
          {selectedSampleIds.size > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowReclassifyModal(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-yellow-600 text-white rounded text-sm hover:bg-yellow-700"
              >
                <Edit2 className="w-4 h-4" />
                Reclassify Domain
              </button>
              <button
                onClick={() => setBulkDeleteConfirm({ open: true })}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded text-sm hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected
              </button>
              <button
                onClick={() => {
                  setShowRunModal(true)
                  setUseSelectedSamples(true)
                }}
                className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              >
                <Play className="w-4 h-4" />
                Run with Selected
              </button>
            </div>
          )}
        </div>
      )}

      {/* Samples Stats */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-6 text-sm text-gray-600 flex-wrap">
          <div>
            <span className="font-medium">Total Samples:</span>{' '}
            <span className="text-gray-900">{samplesPagination.total}</span>
          </div>
          <div>
            <span className="font-medium">Showing:</span>{' '}
            <span className="text-gray-900">
              {Math.min(samplesPagination.offset + 1, samplesPagination.total || 1)} - {Math.min(samplesPagination.offset + samplesPagination.limit, samplesPagination.total || samples.length)}
            </span>
          </div>
          {samplesFilter.languagePair && (
            <div>
              <span className="font-medium">Language:</span>{' '}
              <span className="text-blue-600">{samplesFilter.languagePair}</span>
            </div>
          )}
          {samplesFilter.sourceOrigin && (
            <div>
              <span className="font-medium">Source:</span>{' '}
              <span className="text-blue-600">{samplesFilter.sourceOrigin}</span>
            </div>
          )}
        </div>
      </div>

      {/* Samples Table */}
      <div className="bg-white rounded-lg shadow">
        {samplesLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading samples...</span>
          </div>
        ) : samples.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No samples found.</p>
            <p className="text-sm text-gray-400 mt-2">
              Sync a dataset from the Datasets tab or add custom samples.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    {selectionMode && (
                      <th className="px-3 py-3 text-left w-10">
                        <input
                          type="checkbox"
                          checked={samples.length > 0 && samples.every(s => selectedSampleIds.has(s.id))}
                          onChange={(e) => e.target.checked ? selectAllSamples() : deselectAllSamples()}
                          className="rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">#</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Language Pair</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Text</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {samples.map((sample, idx) => (
                    <tr key={sample.id} className={clsx('hover:bg-gray-50', selectedSampleIds.has(sample.id) && 'bg-blue-50')}>
                      {selectionMode && (
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSampleIds.has(sample.id)}
                            onChange={() => toggleSampleSelection(sample.id)}
                            className="rounded border-gray-300"
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {samplesPagination.offset + idx + 1}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
                          sample.source_origin === 'custom' ? 'bg-purple-100 text-purple-700' :
                          sample.source_origin === 'flores' ? 'bg-green-100 text-green-700' :
                          sample.source_origin === 'wmt' ? 'bg-blue-100 text-blue-700' :
                          sample.source_origin === 'ntrex' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        )}>
                          {sample.source_origin === 'flores' ? 'FLORES+' :
                           sample.source_origin === 'wmt' ? 'WMT' :
                           sample.source_origin === 'ntrex' ? 'NTREX' :
                           sample.source_origin === 'emea' ? 'EMEA' :
                           sample.source_origin === 'europarl' ? 'Europarl' :
                           sample.source_origin === 'opensubtitles' ? 'OpenSub' :
                           sample.source_origin === 'europat' ? 'EuroPat' :
                           sample.source_origin === 'news_commentary' ? 'NewsComm' :
                           sample.source_origin || 'custom'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {sample.language_pair ? (
                          <>
                            <span className="font-medium text-gray-900">{sample.language_pair.split('-')[0]?.toUpperCase()}</span>
                            <span className="text-gray-400 mx-1">→</span>
                            <span className="font-medium text-gray-900">{sample.language_pair.split('-')[1]?.toUpperCase()}</span>
                          </>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">
                          {domainConfig[sample.domain]?.label || sample.domain || 'general'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="max-w-xs truncate" title={sample.source_text}>
                          {sample.source_text}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="max-w-xs truncate" title={sample.reference_translation}>
                          {sample.reference_translation}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {sample.source_origin === 'custom' && (
                            <>
                              <button
                                onClick={() => handleEditSample(sample)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => setDeleteSampleConfirm({ open: true, sample })}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {sample.source_origin !== 'custom' && (
                            <span className="text-gray-400 text-xs">Read-only</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Page {Math.floor(samplesPagination.offset / samplesPagination.limit) + 1} of{' '}
                {Math.ceil(samplesPagination.total / samplesPagination.limit) || 1}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => fetchSamples(Math.max(0, samplesPagination.offset - samplesPagination.limit))}
                  disabled={samplesPagination.offset === 0}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <button
                  onClick={() => fetchSamples(samplesPagination.offset + samplesPagination.limit)}
                  disabled={samplesPagination.offset + samplesPagination.limit >= samplesPagination.total}
                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )

  // Versions tab content
  const renderVersions = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Benchmark Versions</h3>
        <button
          onClick={() => setShowVersionModal(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
          disabled
          title="Coming soon"
        >
          <Plus className="w-4 h-4" />
          New Version
        </button>
      </div>

      <div className="bg-white rounded-lg shadow divide-y">
        {versions.length === 0 ? (
          <div className="p-8 text-center">
            <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No versions created yet.</p>
            <p className="text-sm text-gray-400 mt-2">An initial version will be created automatically.</p>
          </div>
        ) : (
          versions.map((version) => (
            <div key={version.id} className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900">{version.version_code}</h4>
                    {version.is_active && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Active</span>
                    )}
                    {version.deprecated_at && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Deprecated</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">{version.name}</p>
                </div>
                <Settings className="w-5 h-5 text-gray-400" />
              </div>
              {version.description && (
                <p className="mt-2 text-sm text-gray-600">{version.description}</p>
              )}
              <div className="mt-3 flex gap-4 text-sm text-gray-500">
                <span>Created: {new Date(version.created_at).toLocaleDateString()}</span>
                {version.released_at && (
                  <span>Released: {new Date(version.released_at).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading benchmark data...</span>
      </div>
    )
  }

  return (
    <div className="p-6">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quality Benchmarks</h1>
            <p className="text-gray-600 mt-1">
              Evaluate and compare translation quality using COMET, chrF, and BLEU metrics
            </p>
          </div>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { id: 'samples', label: 'Samples', icon: FileText },
            { id: 'datasets', label: 'Datasets', icon: Database },
            { id: 'versions', label: 'Versions', icon: GitBranch }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={clsx(
                'inline-flex items-center gap-2 px-1 py-4 border-b-2 text-sm font-medium',
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard' && renderDashboard()}
      {activeTab === 'samples' && renderSamples()}
      {activeTab === 'datasets' && renderDatasets()}
      {activeTab === 'versions' && renderVersions()}

      {/* New Run Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Start Benchmark Run</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account</label>
                <select
                  value={newRun.account_id}
                  onChange={(e) => setNewRun({ ...newRun, account_id: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                >
                  <option value="">Select account...</option>
                  {accounts.map((account) => (
                    <option key={account.id} value={account.id}>{account.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Language</label>
                  <select
                    value={newRun.source_lang}
                    onChange={(e) => setNewRun({ ...newRun, source_lang: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="en-US">English (en-US)</option>
                    <option value="de-DE">German (de-DE)</option>
                    <option value="fr-FR">French (fr-FR)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Language</label>
                  <select
                    value={newRun.target_lang}
                    onChange={(e) => setNewRun({ ...newRun, target_lang: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <option value="">Select target...</option>
                    <option value="cs-CZ">Czech (cs-CZ)</option>
                    <option value="de-DE">German (de-DE)</option>
                    <option value="fr-FR">French (fr-FR)</option>
                    <option value="es-ES">Spanish (es-ES)</option>
                    <option value="pl-PL">Polish (pl-PL)</option>
                    <option value="it-IT">Italian (it-IT)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sample Limit</label>
                <input
                  type="number"
                  value={newRun.sample_limit}
                  onChange={(e) => setNewRun({ ...newRun, sample_limit: parseInt(e.target.value) || 100 })}
                  min="10"
                  max="1000"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">10-1000 samples per run</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowRunModal(false)}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateRun}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <Play className="w-4 h-4 inline mr-1" />
                Start Run
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sync Dataset Modal */}
      {showSyncModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">Sync Dataset</h3>

            {/* Progress View - shown when sync is running or completed */}
            {(syncProgress.isRunning || syncProgress.completed) ? (
              <div className="space-y-4">
                {/* Progress Header */}
                <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      {syncProgress.isRunning ? 'Syncing in progress...' : 'Sync completed'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {syncProgress.dataset_name || syncForm.dataset_name.toUpperCase()} dataset
                    </p>
                  </div>
                  {syncProgress.isRunning && (
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                  )}
                  {syncProgress.completed && (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  )}
                </div>

                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>
                      {syncProgress.isRunning
                        ? `Syncing: ${syncProgress.currentPair}`
                        : `Completed ${syncProgress.results.filter(r => r.status === 'success').length}/${syncProgress.totalPairs} pairs`
                      }
                    </span>
                    <span>
                      {syncProgress.currentPairIndex + (syncProgress.completed ? 0 : 1)}/{syncProgress.totalPairs}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={clsx(
                        'h-2 rounded-full transition-all duration-300',
                        syncProgress.completed ? 'bg-green-600' : 'bg-blue-600'
                      )}
                      style={{
                        width: `${((syncProgress.currentPairIndex + (syncProgress.completed || syncProgress.isRunning ? 1 : 0)) / syncProgress.totalPairs) * 100}%`
                      }}
                    />
                  </div>
                </div>

                {/* Results List */}
                {syncProgress.results.length > 0 && (
                  <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                    {syncProgress.results.map((result, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-2">
                          {result.status === 'success' ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                          <span className="text-sm font-medium">{result.pair}</span>
                        </div>
                        <div className="text-sm">
                          {result.status === 'success' ? (
                            <span className="text-green-600">{result.samplesImported} samples</span>
                          ) : (
                            <span className="text-red-600" title={result.error}>Failed</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Summary when completed */}
                {syncProgress.completed && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-green-600">
                          {syncProgress.results.filter(r => r.status === 'success').length}
                        </p>
                        <p className="text-xs text-gray-500">Successful</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">
                          {syncProgress.results.filter(r => r.status === 'failed').length}
                        </p>
                        <p className="text-xs text-gray-500">Failed</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-blue-600">
                          {syncProgress.results.reduce((sum, r) => sum + r.samplesImported, 0)}
                        </p>
                        <p className="text-xs text-gray-500">Total Samples</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Close button when completed */}
                {syncProgress.completed && (
                  <div className="flex justify-end">
                    <button
                      onClick={handleCloseSyncModal}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Done
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Configuration View - shown before sync starts */
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dataset</label>
                  <select
                    value={syncForm.dataset_name}
                    onChange={(e) => setSyncForm({ ...syncForm, dataset_name: e.target.value, target_langs: [] })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  >
                    <optgroup label="High Quality (Recommended)">
                      <option value="flores">FLORES+ (200+ languages) - General</option>
                      <option value="ntrex">NTREX (128 languages) - News</option>
                      <option value="wmt">WMT News (15+ pairs) - News</option>
                      <option value="europarl">Europarl (21 languages) - Legal</option>
                      <option value="opus_books">OPUS Books (16 languages) - Literary</option>
                      <option value="tatoeba">Tatoeba (359+ languages) - General</option>
                    </optgroup>
                    <optgroup label="Low Quality (Web-scraped/Noisy)">
                      <option value="emea">⚠️ EMEA (22 languages) - Medical</option>
                      <option value="opensubtitles">⚠️ OpenSubtitles (60+ languages) - Conversational</option>
                      <option value="europat">⚠️ EuroPat (4 languages) - Technical</option>
                      <option value="news_commentary">⚠️ News Commentary (17 languages) - Financial</option>
                    </optgroup>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {syncForm.dataset_name === 'flores' && 'Multi-domain benchmark from Meta AI (news, education, travel)'}
                    {syncForm.dataset_name === 'ntrex' && 'News translation benchmark from Microsoft Research'}
                    {syncForm.dataset_name === 'wmt' && 'WMT19 news test set - gold standard for MT evaluation'}
                    {syncForm.dataset_name === 'europarl' && 'European Parliament proceedings - legal/political language'}
                    {syncForm.dataset_name === 'opus_books' && 'Professional literary translations from copyright-free books'}
                    {syncForm.dataset_name === 'tatoeba' && 'Community-curated high-quality sentence pairs (manually reviewed)'}
                    {syncForm.dataset_name === 'emea' && '⚠️ Web-scraped medical data - may contain noise/errors'}
                    {syncForm.dataset_name === 'opensubtitles' && '⚠️ Web-scraped subtitles - alignment issues, informal'}
                    {syncForm.dataset_name === 'europat' && '⚠️ Web-scraped patent data - may contain noise'}
                    {syncForm.dataset_name === 'news_commentary' && '⚠️ Web-scraped news - may contain noise/errors'}
                  </p>
                </div>

                {/* Language selection info */}
                {datasetLanguagesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading available languages...
                  </div>
                ) : datasetLanguages.length > 0 ? (
                  <p className="text-xs text-green-600">
                    {datasetLanguages.length} languages available for {syncForm.dataset_name.toUpperCase()} (T8R supported)
                  </p>
                ) : null}

                {/* Source Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source Language</label>
                  <select
                    value={syncForm.source_lang}
                    onChange={(e) => setSyncForm({ ...syncForm, source_lang: e.target.value, target_langs: [] })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    disabled={datasetLanguagesLoading}
                  >
                    {datasetLanguages.length > 0 ? (
                      datasetLanguages.map(locale => {
                        const langInfo = supportedLanguages.find(l => l.locale === locale)
                        return (
                          <option key={locale} value={locale}>
                            {langInfo?.name || locale} ({locale})
                          </option>
                        )
                      })
                    ) : supportedLanguages.length > 0 ? (
                      supportedLanguages.map(lang => (
                        <option key={lang.locale} value={lang.locale}>
                          {lang.name} ({lang.locale})
                        </option>
                      ))
                    ) : (
                      <>
                        <option value="en-US">English (en-US)</option>
                        <option value="de-DE">German (de-DE)</option>
                        <option value="fr-FR">French (fr-FR)</option>
                      </>
                    )}
                  </select>
                </div>

                {/* Target Languages - Multi-select with checkboxes */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Target Languages
                      {syncForm.target_langs.length > 0 && (
                        <span className="ml-2 text-blue-600">({syncForm.target_langs.length} selected)</span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={selectAllTargetLangs}
                        className="text-xs text-blue-600 hover:underline"
                        disabled={datasetLanguagesLoading}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={deselectAllTargetLangs}
                        className="text-xs text-gray-500 hover:underline"
                        disabled={datasetLanguagesLoading || syncForm.target_langs.length === 0}
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="border border-gray-300 rounded max-h-48 overflow-y-auto">
                    {datasetLanguagesLoading ? (
                      <div className="p-4 text-center text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                        Loading...
                      </div>
                    ) : (
                      <div className="divide-y">
                        {(datasetLanguages.length > 0 ? datasetLanguages : supportedLanguages.map(l => l.locale))
                          .filter(locale => locale !== syncForm.source_lang)
                          .map(locale => {
                            const langInfo = supportedLanguages.find(l => l.locale === locale)
                            const isSelected = syncForm.target_langs.includes(locale)
                            return (
                              <label
                                key={locale}
                                className={clsx(
                                  'flex items-center gap-3 p-2 cursor-pointer hover:bg-gray-50',
                                  isSelected && 'bg-blue-50'
                                )}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleTargetLang(locale)}
                                  className="rounded border-gray-300 text-blue-600"
                                />
                                <span className="text-sm">
                                  {langInfo?.name || locale}
                                  <span className="text-gray-400 ml-1">({locale})</span>
                                </span>
                              </label>
                            )
                          })}
                      </div>
                    )}
                  </div>
                  {syncForm.target_langs.length === 0 && !datasetLanguagesLoading && (
                    <p className="text-xs text-amber-600 mt-1">
                      Please select at least one target language
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sample Limit (per pair)</label>
                  <input
                    type="number"
                    value={syncForm.limit}
                    onChange={(e) => setSyncForm({ ...syncForm, limit: parseInt(e.target.value) || 200 })}
                    min="100"
                    max="5000"
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {syncForm.target_langs.length > 0 && (
                      <>Estimated total: ~{(syncForm.limit * syncForm.target_langs.length).toLocaleString()} samples</>
                    )}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="clearExisting"
                    checked={syncForm.clear_existing}
                    onChange={(e) => setSyncForm({ ...syncForm, clear_existing: e.target.checked })}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="clearExisting" className="text-sm text-gray-700">
                    Clear existing samples for each language pair
                  </label>
                </div>

                {/* Domain Mappings Section */}
                <div className="border-t pt-4 mt-4">
                  <button
                    type="button"
                    onClick={() => setShowDomainMappings(!showDomainMappings)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    <Settings className="w-4 h-4" />
                    Domain Mappings
                    <span className="text-xs text-gray-500 ml-2">
                      (maps dataset domains to T8R domains)
                    </span>
                  </button>

                  {showDomainMappings && (
                    <div className="mt-3 space-y-2 bg-gray-50 p-3 rounded">
                      <p className="text-xs text-gray-600 mb-2">
                        Configure how dataset domains (news, education, travel) map to T8R Quality Matrix domains.
                      </p>
                      {['news', 'education', 'travel'].map(extDomain => (
                        <div key={extDomain} className="flex items-center gap-2">
                          <span className="text-sm w-24 font-medium text-gray-600">{extDomain}</span>
                          <span className="text-gray-400">→</span>
                          <select
                            value={domainMappings[extDomain] || 'general'}
                            onChange={async (e) => {
                              const newMappings = { ...domainMappings, [extDomain]: e.target.value }
                              setDomainMappings(newMappings)
                              try {
                                await adminApi.updateBenchmarkDomainMappings({ [extDomain]: e.target.value })
                                addToast(`Domain mapping updated: ${extDomain} → ${e.target.value}`, 'success')
                              } catch (err) {
                                console.error('Failed to update domain mapping:', err)
                                addToast('Failed to update domain mapping', 'error')
                              }
                            }}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                          >
                            {supportedDomains.map(d => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={handleCloseSyncModal}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSyncDataset}
                    disabled={syncForm.target_langs.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="w-4 h-4 inline mr-1" />
                    Sync {syncForm.target_langs.length > 0 ? `(${syncForm.target_langs.length} pairs)` : ''}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results Modal */}
      {showResultsModal && selectedRun && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium">Run #{selectedRun.id} Results</h3>
                <p className="text-sm text-gray-500">
                  {selectedRun.source_lang} → {selectedRun.target_lang} | {selectedRun.samples_evaluated} samples
                </p>
              </div>
              <button
                onClick={() => setShowResultsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 bg-gray-50 border-b">
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-sm text-gray-500">COMET</p>
                  <p className="text-xl font-semibold">{selectedRun.avg_comet_score?.toFixed(3) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">chrF</p>
                  <p className="text-xl font-semibold">{selectedRun.avg_chrf_score?.toFixed(1) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">BLEU</p>
                  <p className="text-xl font-semibold">{selectedRun.avg_bleu_score?.toFixed(1) || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Composite</p>
                  <p className="text-xl font-semibold">{selectedRun.composite_score?.toFixed(3) || '-'}</p>
                </div>
              </div>
            </div>

            <div className="overflow-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Translation</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">COMET</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {runResults.map((result) => (
                    <tr key={result.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={result.source_text}>
                        {result.source_text}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500 max-w-xs truncate" title={result.reference_translation}>
                        {result.reference_translation}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-900 max-w-xs truncate" title={result.translated_text}>
                        {result.translated_text}
                      </td>
                      <td className="px-4 py-2">
                        <ScoreBadge score={result.comet_score} metric="comet" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Delete Run Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, run: null })}
        onConfirm={handleDeleteRun}
        title="Delete Benchmark Run"
        message={`Are you sure you want to delete benchmark run #${deleteConfirm.run?.id}? This will also delete all associated results.`}
        confirmText="Delete"
        confirmStyle="danger"
      />

      {/* Delete Sample Confirmation */}
      <ConfirmDialog
        isOpen={deleteSampleConfirm.open}
        onClose={() => setDeleteSampleConfirm({ open: false, sample: null })}
        onConfirm={handleDeleteSample}
        title="Delete Sample"
        message={`Are you sure you want to delete this benchmark sample? This action cannot be undone.`}
        confirmText="Delete"
        confirmStyle="danger"
      />

      {/* Bulk Delete Confirmation */}
      <ConfirmDialog
        isOpen={bulkDeleteConfirm.open}
        onClose={() => setBulkDeleteConfirm({ open: false })}
        onConfirm={handleBulkDelete}
        title="Delete Selected Samples"
        message={`Are you sure you want to delete ${selectedSampleIds.size} selected samples? This action cannot be undone.`}
        confirmText={`Delete ${selectedSampleIds.size} Samples`}
        confirmStyle="danger"
      />

      {/* Reclassify Domain Modal */}
      {showReclassifyModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Reclassify Samples</h3>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Reclassify <strong>{selectedSampleIds.size}</strong> selected samples to a new domain.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Domain
                </label>
                <select
                  value={reclassifyDomain}
                  onChange={(e) => setReclassifyDomain(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Select domain...</option>
                  {Object.entries(domainConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowReclassifyModal(false)
                  setReclassifyDomain('')
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReclassify}
                disabled={!reclassifyDomain}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reclassify {selectedSampleIds.size} Samples
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sample Edit Modal */}
      {showSampleEditModal && (
        <SampleEditModal
          sample={editingSample}
          domains={Object.keys(domainConfig)}
          languagePairs={availableLanguagePairs}
          onSave={handleSaveSample}
          onClose={() => setShowSampleEditModal(false)}
        />
      )}
    </div>
  )
}

// Sample Edit Modal Component
const SampleEditModal = ({ sample, domains, languagePairs, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    domain: sample?.domain || 'general',
    language_pair: sample?.language_pair || 'en-de',
    source_text: sample?.source_text || '',
    reference_translation: sample?.reference_translation || '',
    sample_order: sample?.sample_order || 0,
    is_active: sample?.is_active ?? true,
    notes: sample?.notes || '',
    source_origin: 'custom'
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!formData.source_text.trim() || !formData.reference_translation.trim()) {
      setError('Source text and reference translation are required')
      return
    }

    try {
      setSaving(true)
      await onSave(formData)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save sample')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {sample ? 'Edit Benchmark Sample' : 'Add Benchmark Sample'}
          </h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Domain *
              </label>
              <select
                value={formData.domain}
                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={!!sample}
              >
                {domains.map(d => (
                  <option key={d} value={d}>{domainConfig[d]?.label || d}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language Pair *
              </label>
              <select
                value={formData.language_pair}
                onChange={(e) => setFormData({ ...formData, language_pair: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={!!sample}
              >
                {languagePairs.length > 0 ? (
                  languagePairs.map(lp => (
                    <option key={lp} value={lp}>{lp}</option>
                  ))
                ) : (
                  <>
                    <option value="en-cs">en-cs</option>
                    <option value="en-de">en-de</option>
                    <option value="en-fr">en-fr</option>
                    <option value="en-es">en-es</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Source Text *
            </label>
            <textarea
              value={formData.source_text}
              onChange={(e) => setFormData({ ...formData, source_text: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Enter the source text..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reference Translation *
            </label>
            <textarea
              value={formData.reference_translation}
              onChange={(e) => setFormData({ ...formData, reference_translation: e.target.value })}
              rows={3}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Enter the reference translation..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sample Order
              </label>
              <input
                type="number"
                value={formData.sample_order}
                onChange={(e) => setFormData({ ...formData, sample_order: parseInt(e.target.value) || 0 })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>

            <div className="flex items-center pt-6">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                Active (used in evaluations)
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
              placeholder="Optional notes about this sample..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : (sample ? 'Update Sample' : 'Create Sample')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default QualityBenchmarksPage
